import React, { useState, useEffect } from 'react';
import './WeatherPage.css';

const WeatherPage = () => {
  // 상태 관리 - 기본값을 명확히 설정
  const [locations, setLocations] = useState([]);
  const [newAddress, setNewAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [weatherData, setWeatherData] = useState({});
  const [systemStatus, setSystemStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('locations');

  // 🔥 NEW: 지역 선택 관련 상태
  const [geocodeResult, setGeocodeResult] = useState(null);
  const [showRegionSelection, setShowRegionSelection] = useState(false);
  const [selectedRegionId, setSelectedRegionId] = useState('');

  // API 기본 URL
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

  // 페이지 로드 시 초기 데이터 가져오기
  useEffect(() => {
    loadLocations();
    loadSystemStatus();
  }, []);

  // 위치 목록 로드
  const loadLocations = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/weather/locations`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('API 응답 데이터:', data); // 디버깅용

      if (data.success) {
        // 응답 데이터 구조 확인 및 안전한 설정
        const locationList = data.data || data.locations || [];
        setLocations(Array.isArray(locationList) ? locationList : []);
        setError('');
      } else {
        setError('위치 목록 로드 실패: ' + (data.error || '알 수 없는 오류'));
        setLocations([]); // 실패 시 빈 배열로 설정
      }
    } catch (err) {
      console.error('위치 목록 로드 오류:', err);
      setError('위치 목록 로드 중 오류: ' + err.message);
      setLocations([]); // 오류 시 빈 배열로 설정
    } finally {
      setLoading(false);
    }
  };

  // 시스템 상태 로드
  const loadSystemStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/weather/status`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setSystemStatus(data);
      }
    } catch (err) {
      console.error('시스템 상태 로드 오류:', err);
    }
  };

  // 새 위치 추가
  const addLocation = async () => {
    if (!newAddress.trim()) {
      setError('주소를 입력해주세요');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const response = await fetch(`${API_BASE_URL}/api/weather/locations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ address: newAddress.trim() }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setSuccess(`새 위치가 추가되었습니다: ${data.data?.name || newAddress}`);
        setNewAddress('');
        await loadLocations();
      } else {
        setError('위치 추가 실패: ' + (data.error || '알 수 없는 오류'));
        if (data.existingLocation) {
          setError(`이미 등록된 위치입니다: ${data.existingLocation.name}`);
        }
      }
    } catch (err) {
      console.error('위치 추가 오류:', err);
      setError('위치 추가 중 오류: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // 위치 삭제
  const removeLocation = async (locationId, locationName) => {
    if (!window.confirm(`'${locationName}' 위치를 삭제하시겠습니까?`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/weather/locations/${locationId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setSuccess('위치가 삭제되었습니다');
        await loadLocations();
      } else {
        setError('위치 삭제 실패: ' + (data.error || '알 수 없는 오류'));
      }
    } catch (err) {
      console.error('위치 삭제 오류:', err);
      setError('위치 삭제 중 오류: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // 특정 위치 날씨 조회
  const getLocationWeather = async (locationId) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/weather/weather/${locationId}`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setWeatherData(prev => ({
          ...prev,
          [locationId]: data.weather || data.data
        }));
        setSuccess(`날씨 정보가 업데이트되었습니다: ${data.location?.name || '위치'}`);
      } else {
        setError('날씨 조회 실패: ' + (data.error || '알 수 없는 오류'));
      }
    } catch (err) {
      console.error('날씨 조회 오류:', err);
      setError('날씨 조회 중 오류: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // 전체 날씨 조회
  const getAllWeather = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const response = await fetch(`${API_BASE_URL}/api/weather/weather`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        const weatherMap = {};
        const results = data.data || [];

        results.forEach(result => {
          if (result.status === 'success' && result.location && result.weather) {
            weatherMap[result.location.id] = result.weather;
          }
        });

        setWeatherData(weatherMap);
        setSuccess(`전체 날씨 정보가 업데이트되었습니다 (${data.summary?.success || 0}/${data.summary?.total || 0}개 성공)`);
      } else {
        setError('전체 날씨 조회 실패: ' + (data.error || '알 수 없는 오류'));
      }
    } catch (err) {
      console.error('전체 날씨 조회 오류:', err);
      setError('전체 날씨 조회 중 오류: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // MQTT 전송
  const sendToMQTT = async (locationId = null) => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const url = locationId
        ? `${API_BASE_URL}/api/weather/mqtt/weather/${locationId}`
        : `${API_BASE_URL}/api/weather/mqtt/weather/all`;

      const response = await fetch(url, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        if (locationId) {
          setSuccess(`MQTT 전송 성공: ${data.location} (${data.temperature}°C)`);
        } else {
          setSuccess(`MQTT 일괄 전송 성공: ${data.summary?.success || 0}/${data.summary?.total || 0}개 위치`);
        }
      } else {
        setError('MQTT 전송 실패: ' + (data.error || '알 수 없는 오류'));
      }
    } catch (err) {
      console.error('MQTT 전송 오류:', err);
      setError('MQTT 전송 중 오류: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // 🔥 수정된 주소 검증 - 지역 선택 UI 표시
  const validateAddress = async () => {
    if (!newAddress.trim()) {
      setError('주소를 입력해주세요');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');
      setShowRegionSelection(false);
      setGeocodeResult(null);
      setSelectedRegionId('');

      const response = await fetch(`${API_BASE_URL}/api/weather/geocode`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ address: newAddress.trim() }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        const locationData = data.data;
        setGeocodeResult(data);

        if (locationData.hasMultipleRegions && locationData.availableRegions.length > 1) {
          // 🔥 여러 지역이 있는 경우 선택 UI 표시
          setShowRegionSelection(true);
          setSuccess(`주소 확인됨: ${locationData.name} (${locationData.availableRegions.length}개 지역 선택 가능)`);
        } else {
          // 단일 지역인 경우 바로 완료
          setSuccess(`주소 확인됨: ${locationData.name} (격자좌표: ${locationData.nx}, ${locationData.ny})`);
          if (locationData.availableRegions.length === 1) {
            setSelectedRegionId(locationData.availableRegions[0].regionId);
          }
        }
      } else {
        setError('주소 확인 실패: ' + (data.error || '알 수 없는 오류'));
      }
    } catch (err) {
      console.error('주소 확인 오류:', err);
      setError('주소 확인 중 오류: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // 🔥 NEW: 선택된 지역으로 위치 추가
  const addLocationWithSelectedRegion = async () => {
    if (!geocodeResult || !selectedRegionId) {
      setError('지역을 선택해주세요');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const response = await fetch(`${API_BASE_URL}/api/weather/locations/with-region`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          address: newAddress.trim(),
          selectedRegionId: selectedRegionId
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setSuccess(`새 위치가 추가되었습니다: ${data.data?.name || newAddress} (${data.selectedRegion?.regionName})`);
        setNewAddress('');
        setShowRegionSelection(false);
        setGeocodeResult(null);
        setSelectedRegionId('');
        await loadLocations();
      } else {
        setError('위치 추가 실패: ' + (data.error || '알 수 없는 오류'));
        if (data.existingLocation) {
          setError(`이미 등록된 위치입니다: ${data.existingLocation.name}`);
        }
      }
    } catch (err) {
      console.error('위치 추가 오류:', err);
      setError('위치 추가 중 오류: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // 날씨 아이콘 URL 생성 (기상청 데이터용)
  const getWeatherIcon = (weatherDescription) => {
    // 기상청 날씨 상태에 따른 이모지 아이콘
    if (weatherDescription.includes('맑음')) return '☀️';
    if (weatherDescription.includes('구름많음')) return '⛅';
    if (weatherDescription.includes('흐림')) return '☁️';
    if (weatherDescription.includes('비')) return '🌧️';
    if (weatherDescription.includes('눈')) return '❄️';
    if (weatherDescription.includes('소나기')) return '🌦️';
    return '🌤️'; // 기본값
  };

  // 온도 색상 결정
  const getTemperatureColor = (temp) => {
    if (temp <= 0) return '#4fc3f7'; // 매우 추움 - 연한 파랑
    if (temp <= 10) return '#29b6f6'; // 추움 - 파랑
    if (temp <= 20) return '#66bb6a'; // 쌀쌀함 - 연한 초록
    if (temp <= 25) return '#9ccc65'; // 적당함 - 초록
    if (temp <= 30) return '#ffca28'; // 따뜻함 - 노랑
    if (temp <= 35) return '#ffa726'; // 더움 - 주황
    return '#ef5350'; // 매우 더움 - 빨강
  };

  // 🔥 LocationCard 컴포넌트 수정 - 격자 좌표 기반 정보 표시
  const LocationCard = ({ location, onDelete, onGetWeather }) => {
    return (
      <div className="location-card">
        <div className="location-header">
          {/* 🔥 격자 좌표를 메인 타이틀로 */}
          <h4>{location.name}</h4>  {/* 격자(102,84) */}
          <button onClick={() => onDelete(location.id, location.displayName || location.name)} className="btn-delete">
            🗑️
          </button>
        </div>

        <div className="location-info">
          {/* 🔥 GPS 좌표 표시 */}
          {location.gpsDisplayName && (
            <p><strong>GPS 좌표:</strong> {location.gpsDisplayName}</p>
          )}

          {/* 🔥 원본 주소 표시 */}
          <p><strong>원본 주소:</strong> {location.originalAddress}</p>

          {/* 🔥 NEW: 선택된 지역 정보 표시 */}
          {location.selectedRegionId && (
            <p><strong>선택된 지역:</strong> {location.selectedRegionName} ({location.selectedRegionId})</p>
          )}

          {/* 기존 정보들 */}
          <p><strong>격자 좌표:</strong> ({location.nx}, {location.ny})</p>
          <p><strong>MQTT 토픽:</strong> <code>{location.topic}</code></p>
          <p><strong>추가일:</strong> {new Date(location.addedAt).toLocaleString('ko-KR')}</p>

          {location.lastWeatherUpdate && (
            <p><strong>마지막 업데이트:</strong> {new Date(location.lastWeatherUpdate).toLocaleString('ko-KR')}</p>
          )}

          {/* 🔥 중복 격자 정보 표시 - 지역 ID 기반으로 수정 */}
          {location.isDuplicateGrid && location.selectedRegionId && (
            <p className="duplicate-grid-info">
              <strong>격자 공유:</strong> {location.sharedWeatherWith}와 동일한 날씨 데이터
            </p>
          )}
        </div>

        <div className="location-actions">
          <button onClick={() => onGetWeather(location.id)} className="btn-weather">
            🌤️ 날씨 조회
          </button>
        </div>
      </div>
    );
  };

  // 안전한 배열 길이 체크
  const safeLocationsLength = Array.isArray(locations) ? locations.length : 0;

  return (
    <div className="weather-page">
      <div className="weather-header">
        <h1>🌤️ 날씨 모니터링 시스템</h1>
        <p>한국 기상청 API를 활용한 실시간 날씨 정보 관리</p>
      </div>

      {/* 탭 네비게이션 */}
      <div className="weather-tabs">
        <button
          className={`tab-button ${activeTab === 'locations' ? 'active' : ''}`}
          onClick={() => setActiveTab('locations')}
        >
          📍 위치 관리
        </button>
        <button
          className={`tab-button ${activeTab === 'weather' ? 'active' : ''}`}
          onClick={() => setActiveTab('weather')}
        >
          🌦️ 날씨 정보
        </button>
        <button
          className={`tab-button ${activeTab === 'mqtt' ? 'active' : ''}`}
          onClick={() => setActiveTab('mqtt')}
        >
          📡 MQTT 전송
        </button>
        <button
          className={`tab-button ${activeTab === 'status' ? 'active' : ''}`}
          onClick={() => setActiveTab('status')}
        >
          📊 시스템 상태
        </button>
      </div>

      {/* 알림 메시지 */}
      {error && (
        <div className="alert alert-error">
          ❌ {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          ✅ {success}
        </div>
      )}

      {/* 위치 관리 탭 */}
      {activeTab === 'locations' && (
        <div className="tab-content">
          <div className="location-add-section">
            <h3>새 위치 추가</h3>
            <div className="add-location-form">
              <input
                type="text"
                value={newAddress}
                onChange={(e) => setNewAddress(e.target.value)}
                placeholder="주소를 입력하세요 (예: 울산광역시 남구 달동)"
                onKeyPress={(e) => e.key === 'Enter' && validateAddress()}
                disabled={loading}
              />
              <button onClick={validateAddress} disabled={loading} className="btn-secondary">
                주소 확인
              </button>
              {!showRegionSelection && (
                <button onClick={addLocation} disabled={loading} className="btn-primary">
                  위치 추가
                </button>
              )}
            </div>

            {/* 🔥 NEW: 지역 선택 UI */}
            {showRegionSelection && geocodeResult && (
              <div className="region-selection-section">
                <h4>📍 지역 선택</h4>
                <p>해당 격자에는 여러 지역이 있습니다. 원하는 지역을 선택해주세요:</p>

                <div className="region-selection-info">
                  <p><strong>격자 좌표:</strong> ({geocodeResult.data.nx}, {geocodeResult.data.ny})</p>
                  <p><strong>GPS 좌표:</strong> {geocodeResult.data.gpsDisplayName}</p>
                </div>

                <div className="region-options">
                  {geocodeResult.data.availableRegions.map((region) => (
                    <label key={region.regionId} className="region-option">
                      <input
                        type="radio"
                        name="regionSelection"
                        value={region.regionId}
                        checked={selectedRegionId === region.regionId}
                        onChange={(e) => setSelectedRegionId(e.target.value)}
                        disabled={loading}
                      />
                      <span className="region-info">
                        <strong>{region.regionName}</strong>
                        <small>({region.regionId})</small>
                      </span>
                    </label>
                  ))}
                </div>

                <div className="region-selection-actions">
                  <button
                    onClick={() => {
                      setShowRegionSelection(false);
                      setGeocodeResult(null);
                      setSelectedRegionId('');
                    }}
                    disabled={loading}
                    className="btn-secondary"
                  >
                    취소
                  </button>
                  <button
                    onClick={addLocationWithSelectedRegion}
                    disabled={loading || !selectedRegionId}
                    className="btn-primary"
                  >
                    선택한 지역으로 추가
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="locations-list-section">
            <div className="section-header">
              <h3>등록된 위치 ({safeLocationsLength}개)</h3>
              <button onClick={loadLocations} disabled={loading} className="btn-refresh">
                🔄 새로고침
              </button>
            </div>

            {safeLocationsLength === 0 ? (
              <div className="empty-state">
                <p>등록된 위치가 없습니다. 새 위치를 추가해보세요!</p>
              </div>
            ) : (
              <div className="locations-grid">
                {locations.map((location, index) => (
                  <LocationCard
                    key={location.id || `${location.name}-${index}`}
                    location={location}
                    onDelete={removeLocation}
                    onGetWeather={getLocationWeather}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 날씨 정보 탭 */}
      {activeTab === 'weather' && (
        <div className="tab-content">
          <div className="weather-section">
            <div className="section-header">
              <h3>날씨 정보</h3>
              <button onClick={getAllWeather} disabled={loading} className="btn-primary">
                🌦️ 전체 날씨 조회
              </button>
            </div>

            {Object.keys(weatherData).length === 0 ? (
              <div className="empty-state">
                <p>날씨 정보가 없습니다. 위치를 추가하고 날씨를 조회해보세요!</p>
              </div>
            ) : (
              <div className="weather-grid">
                {locations.map((location, index) => {
                  const weather = weatherData[location.id];
                  if (!weather) return null;

                  return (
                    <div key={location.id || `weather-${location.name}-${index}`} className="weather-card">
                      <div className="weather-header">
                        <h4>{location.name}</h4>
                        <div className="weather-main">
                          <span className="weather-icon">
                            {getWeatherIcon(weather.current?.weather || '맑음')}
                          </span>
                          <div
                            className="temperature"
                            style={{ color: getTemperatureColor(weather.current?.temperature || 20) }}
                          >
                            {weather.current?.temperature || 0}°C
                          </div>
                        </div>
                      </div>

                      <div className="weather-info">
                        <p><strong>주소:</strong> {location.originalAddress || location.displayName || '주소 정보 없음'}</p>
                        <p><strong>날씨:</strong> {weather.current?.weather || '정보 없음'}</p>
                        <p><strong>습도:</strong> {weather.current?.humidity || 0}%</p>
                        <p><strong>바람:</strong> {weather.current?.windSpeed || 0} m/s</p>
                        <p><strong>강수량:</strong> {weather.current?.precipitation || 0} mm</p>
                        <p><strong>업데이트:</strong> {weather.current?.timestampKR || '정보 없음'}</p>
                      </div>

                      {weather.forecast && (
                        <div className="weather-forecast">
                          <h5>오늘 예보</h5>
                          <div className="forecast-info">
                            <p><strong>최고:</strong> {weather.forecast.today?.maxTemp || '정보 없음'}°C</p>
                            <p><strong>최저:</strong> {weather.forecast.today?.minTemp || '정보 없음'}°C</p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* MQTT 전송 탭 */}
      {activeTab === 'mqtt' && (
        <div className="tab-content">
          <div className="mqtt-section">
            <h3>MQTT 전송</h3>
            <p>날씨 정보를 MQTT 브로커로 전송합니다. 각 위치별로 /weather/grid/nx/ny 형식의 토픽을 사용합니다.</p>

            <div className="mqtt-actions">
              <button
                onClick={() => sendToMQTT()}
                disabled={loading || safeLocationsLength === 0}
                className="btn-primary btn-large"
              >
                🚀 전체 위치 MQTT 전송
              </button>
            </div>

            <div className="mqtt-locations">
              <h4>개별 위치 MQTT 전송</h4>
              {safeLocationsLength === 0 ? (
                <p>등록된 위치가 없습니다.</p>
              ) : (
                <div className="mqtt-grid">
                  {locations.map((location, index) => (
                    <div key={location.id || `mqtt-${location.name}-${index}`} className="mqtt-item">
                      <div className="mqtt-info">
                        <h5>{location.name}</h5>
                        <p><strong>토픽:</strong> <code>{location.topic}</code></p>
                        {weatherData[location.id] && (
                          <p><strong>현재 날씨:</strong> {weatherData[location.id].current?.temperature}°C, {weatherData[location.id].current?.weather}</p>
                        )}
                      </div>
                      <button
                        onClick={() => sendToMQTT(location.id)}
                        disabled={loading}
                        className="btn-mqtt"
                      >
                        📡 MQTT 전송
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 시스템 상태 탭 */}
      {activeTab === 'status' && (
        <div className="tab-content">
          <div className="status-section">
            <h3>시스템 상태</h3>

            {systemStatus ? (
              <div className="status-grid">
                <div className="status-card">
                  <h4>🌤️ 날씨 시스템</h4>
                  <div className="status-info">
                    <p><strong>등록된 위치:</strong> {safeLocationsLength}개</p>
                    <p><strong>마지막 업데이트:</strong> {systemStatus.lastUpdate ? new Date(systemStatus.lastUpdate).toLocaleString('ko-KR') : '없음'}</p>
                    <p><strong>API 제공업체:</strong> 한국 기상청</p>
                    <p><strong>서비스:</strong> {systemStatus.service || '한국 기상청 날씨 서비스'}</p>
                    <p><strong>라이선스:</strong> {systemStatus.license || '공공누리 제1유형'}</p>
                  </div>
                </div>

                <div className="status-card">
                  <h4>📡 MQTT 상태</h4>
                  <div className="status-info">
                    <p><strong>연결 상태:</strong>
                      <span className="status-indicator connected">
                        🟢 대기 중
                      </span>
                    </p>
                    <p><strong>토픽 형식:</strong> /weather/grid/nx/ny</p>
                    <p><strong>전송 가능 위치:</strong> {safeLocationsLength}개</p>
                  </div>
                </div>

                <div className="status-card">
                  <h4>⚙️ 시스템 정보</h4>
                  <div className="status-info">
                    <p><strong>상태:</strong>
                      <span className="status-indicator connected">
                        🟢 {systemStatus.status || '운영 중'}
                      </span>
                    </p>
                    <p><strong>지원 지역:</strong> {systemStatus.supportedRegions || '대한민국'}</p>
                    <p><strong>총 지원 위치:</strong> 무제한</p>
                    <p><strong>타임스탬프:</strong> {new Date(systemStatus.timestamp || Date.now()).toLocaleString('ko-KR')}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="loading-state">
                <p>시스템 상태를 로드하는 중...</p>
              </div>
            )}

            <div className="status-actions">
              <button onClick={loadSystemStatus} disabled={loading} className="btn-refresh">
                🔄 상태 새로고침
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 로딩 오버레이 */}
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>처리 중...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default WeatherPage;